import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, ControlledTextField } from '../../../../shared/components';
import { FINAL_AMOUNT_SOURCE, QUOTE_AS_PER } from '../../../domain/work.constants';
import AccountingIdentifiersSection from '../components/AccountingIdentifiersSection';
import GrossTotalSection from '../components/GrossTotalSection';
import MilestonesSection from '../components/MilestonesSection';
import QuoteAsPerSection from '../components/QuoteAsPerSection';
import SiteVisitSection from '../components/SiteVisitSection';

const GST_RATE = 0.18;

export interface StepAccountingProps {
  /**
   * Hides Job Code and Document Reference Number. They identify the job rather than the
   * quotation, are already set on the Edit Job screen, and a job can now carry several
   * quotations — so repeating them on each one would imply they vary per quote.
   */
  hideIdentifiers?: boolean;
  /** Hides the Milestones table — milestones belong to the job, not to a quotation. */
  hideMilestones?: boolean;
}

/**
 * Both flags default to false, so the New Job Creation wizard renders exactly as before:
 * it still collects the job code, the document reference and the milestones, because on
 * that screen there is no job yet to have carried them.
 */
export default function StepAccounting({
  hideIdentifiers = false,
  hideMilestones = false,
}: StepAccountingProps = {}) {
  const { watch, setValue, resetField, getValues } = useFormContext();
  const asPer = watch('quote.asPer');
  const area = watch('quote.area');
  const totalAreaSqFt = watch('totalAreaSqFt');
  const sqftRate = watch('quote.sqft.rate');
  const percentageValue = watch('quote.percentage.value');
  const constructionRate = watch('quote.percentage.constructionRate');
  const constructionCostManual = watch('quote.percentage.constructionCostManual');
  const sqftFinalSource = watch('quote.sqft.finalSource');
  const percentageFinalSource = watch('quote.percentage.finalSource');
  const engineerCharge = watch('siteVisit.engineer.charge');
  const directorCharge = watch('siteVisit.director.charge');
  const discountValue = watch('discount.value');
  const siteVisitInScope = watch('siteVisit.inScope');
  const sqftFinalValue = watch('quote.sqft.finalValue');
  const percentageFinalValue = watch('quote.percentage.finalValue');
  const lumpsumValue = watch('quote.lumpsum.value');

  const derivedConstructionCost = useMemo(() => {
    if (!area || !constructionRate) return null;
    return Number(area) * Number(constructionRate);
  }, [area, constructionRate]);

  const isSqFt = asPer === QUOTE_AS_PER.SQFT;
  const isPercentage = asPer === QUOTE_AS_PER.PERCENTAGE;

  const calculatedValue = useMemo(() => {
    if (isSqFt) {
      if (!area || !sqftRate) return null;
      return Number(area) * Number(sqftRate);
    }

    if (isPercentage) {
      if (!percentageValue) return null;
      const constructionCost = derivedConstructionCost ?? constructionCostManual;
      if (!constructionCost) return null;
      return (Number(constructionCost) * Number(percentageValue)) / 100;
    }

    return null;
  }, [
    isSqFt,
    isPercentage,
    area,
    sqftRate,
    percentageValue,
    derivedConstructionCost,
    constructionCostManual,
  ]);

  const projectCostBeforeDiscount = useMemo(() => {
    if (asPer === QUOTE_AS_PER.SQFT) return sqftFinalValue || null;
    if (asPer === QUOTE_AS_PER.PERCENTAGE) return percentageFinalValue || null;
    if (asPer === QUOTE_AS_PER.LUMPSUM) return lumpsumValue || null;
    return null;
  }, [asPer, sqftFinalValue, percentageFinalValue, lumpsumValue]);

  const projectCostAfterDiscount = useMemo(() => {
    if (!projectCostBeforeDiscount) return null;
    if (discountValue === '' || discountValue == null) {
      return projectCostBeforeDiscount;
    }
    return Number(projectCostBeforeDiscount) - Number(discountValue);
  }, [projectCostBeforeDiscount, discountValue]);

  const gstAmount = useMemo(() => {
    if (!projectCostAfterDiscount) return null;
    return Number(projectCostAfterDiscount) * GST_RATE;
  }, [projectCostAfterDiscount]);

  /** Rounded off to the rupee, as `computeQuoteTotals` saves it. */
  const grossTotal = useMemo(() => {
    if (!projectCostAfterDiscount || !gstAmount) return null;
    return Math.round(Number(projectCostAfterDiscount) + Number(gstAmount));
  }, [projectCostAfterDiscount, gstAmount]);

  /**
   * The quotation quotes the building the Work Details screen described, so its area is that
   * screen's total area rather than a second number typed here. `quote.area` is still the
   * field everything downstream reads — the totals, the payload — it is just no longer
   * anyone's to type.
   */
  useEffect(() => {
    const next = totalAreaSqFt ?? '';
    if (getValues('quote.area') === next) return;

    setValue('quote.area', next, { shouldDirty: false, shouldValidate: false });
  }, [totalAreaSqFt, getValues, setValue]);

  useEffect(() => {
    if (!asPer) return;

    resetField('quote.sqft');
    resetField('quote.percentage');
    resetField('quote.lumpsum');

    if (asPer === QUOTE_AS_PER.SQFT) {
      setValue('quote.sqft.finalSource', FINAL_AMOUNT_SOURCE.CALCULATED, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (asPer === QUOTE_AS_PER.PERCENTAGE) {
      setValue('quote.percentage.finalSource', FINAL_AMOUNT_SOURCE.CALCULATED, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [asPer, resetField, setValue]);

  /**
   * Area x Construction Rate is the construction cost, and `QuoteAsPerSection` shows that figure
   * in the Construction cost box itself. The typed-in cost is cleared behind it so that
   * dropping the area or the rate later falls back to an empty box rather than to a stale
   * number nobody entered.
   */
  useEffect(() => {
    if (asPer !== QUOTE_AS_PER.PERCENTAGE) return;

    if (derivedConstructionCost) {
      resetField('quote.percentage.constructionCostManual');
    }
  }, [asPer, derivedConstructionCost, resetField]);

  useEffect(() => {
    if (asPer !== QUOTE_AS_PER.SQFT) return;
    if (!calculatedValue) return;

    setValue('quote.sqft.finalSource', FINAL_AMOUNT_SOURCE.CALCULATED, {
      shouldDirty: false,
      shouldValidate: false,
    });

    setValue('quote.sqft.finalValue', calculatedValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [asPer, calculatedValue, setValue]);

  useEffect(() => {
    if (asPer !== QUOTE_AS_PER.PERCENTAGE) return;
    if (!calculatedValue) return;

    setValue('quote.percentage.finalSource', FINAL_AMOUNT_SOURCE.CALCULATED, {
      shouldDirty: false,
      shouldValidate: false,
    });

    setValue('quote.percentage.finalValue', calculatedValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [asPer, calculatedValue, setValue]);

  useEffect(() => {
    if (asPer !== QUOTE_AS_PER.SQFT) return;
    if (sqftFinalSource !== FINAL_AMOUNT_SOURCE.MANUAL) return;

    const current = getValues('quote.sqft.finalValue');
    if (current) return;

    if (calculatedValue) {
      setValue('quote.sqft.finalValue', calculatedValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [asPer, sqftFinalSource, calculatedValue, getValues, setValue]);

  useEffect(() => {
    if (asPer !== QUOTE_AS_PER.PERCENTAGE) return;
    if (percentageFinalSource !== FINAL_AMOUNT_SOURCE.MANUAL) return;

    const current = getValues('quote.percentage.finalValue');
    if (current) return;

    if (calculatedValue) {
      setValue('quote.percentage.finalValue', calculatedValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [asPer, percentageFinalSource, calculatedValue, getValues, setValue]);

  useEffect(() => {
    if (!engineerCharge) {
      resetField('siteVisit.engineer.freeVisits');
    }
  }, [engineerCharge, resetField]);

  useEffect(() => {
    if (!directorCharge) {
      resetField('siteVisit.director.freeVisits');
    }
  }, [directorCharge, resetField]);

  useEffect(() => {
    if (!projectCostBeforeDiscount) {
      resetField('discount.value');
      return;
    }

    const discount = Number(discountValue);
    if (discount && discount > Number(projectCostBeforeDiscount)) {
      resetField('discount.value');
    }
  }, [projectCostBeforeDiscount, discountValue, resetField]);

  return (
    <>
      {!hideIdentifiers && <AccountingIdentifiersSection />}
      <Card title='Accounting'>
        <div className='column gap-4'>
          <QuoteAsPerSection
            asPer={asPer}
            calculatedValue={calculatedValue}
            derivedConstructionCost={derivedConstructionCost}
          />
          <SiteVisitSection
            engineerCanHaveFreeVisits={!!engineerCharge}
            directorCanHaveFreeVisits={!!directorCharge}
            inScope={siteVisitInScope !== false}
          />
          {/*
            "Add Discount" is no longer offered: the firm quotes the fee as it stands.
            `discount.value` stays at its blank default, so the totals below and the saved
            quotation carry no discount.
          */}
          <GrossTotalSection
            baseAmount={projectCostAfterDiscount}
            gstAmount={gstAmount}
            grossTotal={grossTotal}
          />
          {!hideMilestones && <MilestonesSection grossTotal={grossTotal} />}
          <ControlledTextField
            id='accountingNotes'
            name='details.accountingNotes'
            // Milestones are section 4; without them Notes moves up rather than leaving a gap.
            label={hideMilestones ? '4. Notes' : '5. Notes'}
            placeholder='Add any internal notes for this step (optional)'
            isMultiline
            rows={4}
            rules={{
              maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' },
            }}
            labelVariant='primary'
            showCharCount
          />
        </div>
      </Card>
    </>
  );
}
